import React, { useRef, useCallback, useState, useEffect } from 'react';
import * as Popover from '@radix-ui/react-popover';
import { motion } from 'framer-motion';

/** Delay before opening (avoids opening on accidental mouse pass). */
const OPEN_DELAY_MS = 600;
/** Delay before closing so user can move to popover content. */
const CLOSE_DELAY_MS = 150;

/** Popover container (no animation; animation is on motion.div so it always runs). */
export const SIDEBAR_POPOVER_CONTENT_CLASS =
  'z-[99999999999999] overflow-hidden rounded-xl bg-white dark:bg-gray-900 p-0';

/** Premium elevation: layered shadow + hairline edge + top highlight so it reads as floating content. */
const POPOVER_SHADOW_CLASS =
  'h-full w-full overflow-hidden !rounded-xl border !border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900' +
  ' !shadow-xl';

/** Subtle enter/exit only for the popover (fade + scale). */
const popoverVariants = {
  hidden: { opacity: 0, scale: 0.96, pointerEvents: 'none' as const },
  visible: {
    opacity: 1,
    scale: 1,
    pointerEvents: 'auto' as const,
    transition: {
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    pointerEvents: 'none' as const,
    transition: {
      duration: 0.15,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  },
};

export interface SidebarHoverPopoverProps {
  /** Trigger element (e.g. the sidebar item). */
  children: React.ReactNode;
  /** Content rendered inside the popover. Will be wrapped with id="search-app" and shared styles. */
  content: React.ReactNode;
  /** Optional title shown at the top of the popover. */
  title?: string;
  /** Preferred side; flips to the other side if not enough space. */
  preferredSide?: 'left' | 'right';
  /** Optional; when not set, popover width is determined by content (with min/max bounds). */
  width?: string;
}

/**
 * Reusable hover-triggered popover for mini sidebar items.
 * Opens on hover, uses id="search-app" and shared CSS so it works in the app context.
 * By default opens to the left; flips to right when needed for visibility.
 */
export const SidebarHoverPopover: React.FC<SidebarHoverPopoverProps> = ({
  children,
  content,
  title,
  preferredSide = 'right',
  width,
}) => {
  const [open, setOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const clearOpenDelay = useCallback(() => {
    if (openDelayRef.current !== null) {
      clearTimeout(openDelayRef.current);
      openDelayRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  useEffect(() => {
    return () => {
      clearOpenDelay();
      clearCloseTimeout();
    };
  }, [clearOpenDelay, clearCloseTimeout]);

  const handleTriggerEnter = useCallback(() => {
    clearCloseTimeout();
    clearOpenDelay();
    openDelayRef.current = setTimeout(() => {
      openDelayRef.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  }, [clearCloseTimeout, clearOpenDelay]);

  const handleContentEnter = useCallback(() => {
    clearCloseTimeout();
    setOpen(true);
  }, [clearCloseTimeout]);

  const handleContentLeave = useCallback(() => {
    scheduleClose();
  }, [scheduleClose]);

  const handleTriggerLeave = useCallback(() => {
    clearOpenDelay();
    scheduleClose();
  }, [clearOpenDelay, scheduleClose]);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <span
          className="block w-full"
          onMouseEnter={handleTriggerEnter}
          onMouseLeave={handleTriggerLeave}
        >
          {children}
        </span>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          id="search-app"
          side={preferredSide}
          align="start"
          sideOffset={8}
          collisionPadding={16}
          onMouseEnter={handleContentEnter}
          onMouseLeave={handleContentLeave}
          style={{
            zIndex: 99999999999999,
            ...(width
              ? { width }
              : { minWidth: 200, maxWidth: 'min(400px, 92vw)' }),
          }}
          onCloseAutoFocus={(e) => e.preventDefault()}
          forceMount
          asChild
        >
          <motion.div
            className={SIDEBAR_POPOVER_CONTENT_CLASS}
            style={width ? { width } : undefined}
            onMouseEnter={handleContentEnter}
            onMouseLeave={handleContentLeave}
            variants={popoverVariants}
            initial="hidden"
            animate={open ? 'visible' : 'exit'}
          >
            <div className={POPOVER_SHADOW_CLASS}>
              <div className="max-h-[min(70vh,420px)] overflow-y-auto">
                {title?.trim() ? (
                  <div className="px-4 pt-4 pb-2">
                    <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {title}
                    </h3>
                  </div>
                ) : null}
                <div className={title?.trim() ? 'px-4 pt-0 pb-4' : 'p-4'}>
                  {content}
                </div>
              </div>
            </div>
          </motion.div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
